import type { KeyboardEvent } from 'react';
import { cn } from './cn';

export type WheelGroup = 'sales' | 'deal' | 'prod' | 'care';

export interface WheelNode {
  id: string;
  label: string;
  group: WheelGroup;
  /** Badge top right; hidden at 0. */
  count: number;
  /** 🔥 with this number bottom left; hidden at 0. */
  burning: number;
  dimmed?: boolean;
}

/** Group colours (the legend uses the same). */
export const WHEEL_COLORS: Record<WheelGroup, string> = {
  sales: '#2563eb',
  deal: '#d97706',
  prod: '#059669',
  care: '#7c3aed',
};

const C = 450;
const R = 330;
const NODE_R = 46;
const INNER_R = 150;
const SUB_R = 30;

const at = (r: number, deg: number): [number, number] => [
  C + r * Math.cos((deg * Math.PI) / 180),
  C + r * Math.sin((deg * Math.PI) / 180),
];
const arc = (r: number, from: number, to: number) => {
  const [x1, y1] = at(r, from);
  const [x2, y2] = at(r, to);
  return `M${x1},${y1} A${r},${r} 0 0 1 ${x2},${y2}`;
};
/** A straight arrow between two circles, ending outside both. */
const link = ([x1, y1]: [number, number], r1: number, [x2, y2]: [number, number], r2: number) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const d = Math.hypot(dx, dy);
  return `M${x1 + (dx / d) * (r1 + 4)},${y1 + (dy / d) * (r1 + 4)} L${x2 - (dx / d) * (r2 + 6)},${y2 - (dy / d) * (r2 + 6)}`;
};

/**
 * The business cycle (FR-BIZ-001, FR-BIZ-010, FR-BIZ-013/014): outer steps around the ring with
 * arrows, the production circle inside, a "change request" arrow from `changeFrom` back into
 * production. Nodes are buttons (`aria-pressed` when selected); counts and 🔥 as badges.
 */
export function CycleWheel({
  label,
  outer,
  inner,
  production,
  changeFrom,
  selected,
  onSelect,
  centerTitle,
  centerSubtitle,
  changeLabel,
  nodeLabel,
}: {
  label: string;
  outer: readonly WheelNode[];
  inner: readonly WheelNode[];
  /** The outer node the production circle belongs to. */
  production: string;
  changeFrom: string;
  selected: string | null;
  onSelect: (id: string) => void;
  centerTitle: string;
  centerSubtitle: string;
  changeLabel: string;
  /** Accessible name of a node ("9.3 Izstrāde: 2 uzdevumi"). */
  nodeLabel: (node: WheelNode) => string;
}) {
  const angle = (i: number) => -90 + i * (360 / outer.length);
  const innerAngle = (k: number) => 150 + k * (360 / inner.length);
  const prodIndex = outer.findIndex((n) => n.id === production);
  const careIndex = outer.findIndex((n) => n.id === changeFrom);
  const prodAt = at(R, angle(prodIndex));
  const careAt = at(R, angle(careIndex));
  const firstSub = at(INNER_R, innerAngle(0));
  const lastSub = at(INNER_R, innerAngle(inner.length - 1));

  const node = (n: WheelNode, [x, y]: [number, number], r: number, small: boolean) => {
    const color = WHEEL_COLORS[n.group];
    const isSelected = selected === n.id;
    const press = () => onSelect(n.id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        press();
      }
    };
    const badgeR = small ? 11 : 13;
    return (
      <g
        key={n.id}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        aria-label={nodeLabel(n)}
        data-step={n.id}
        data-dimmed={n.dimmed || undefined}
        onClick={press}
        onKeyDown={onKey}
        className={cn(
          'cursor-pointer outline-none focus-visible:[&>circle:first-child]:stroke-[5]',
          n.dimmed && 'opacity-35',
        )}
      >
        <circle
          cx={x}
          cy={y}
          r={r}
          fill={isSelected ? color : '#fff'}
          stroke={color}
          strokeWidth={isSelected ? 4 : 3}
        />
        <text
          x={x}
          y={small ? y + 5 : y - 2}
          textAnchor="middle"
          className="font-bold"
          fontSize={small ? 12.5 : 17}
          fill={isSelected ? '#fff' : '#1f2937'}
        >
          {n.id}
        </text>
        <text
          x={x}
          y={small ? y + r + 15 : y + 15}
          textAnchor="middle"
          fontSize={small ? 12.5 : n.label.length > 11 ? 11.5 : 13}
          fill={isSelected && !small ? '#fff' : '#374151'}
        >
          {n.label}
        </text>
        {n.count > 0 && (
          <g data-badge="count">
            <circle cx={x + r * 0.72} cy={y - r * 0.72} r={badgeR} fill={color} />
            <text
              x={x + r * 0.72}
              y={y - r * 0.72 + 4}
              textAnchor="middle"
              fontSize={small ? 11 : 13}
              fill="#fff"
              className="font-bold"
            >
              {n.count}
            </text>
          </g>
        )}
        {n.burning > 0 && (
          <g data-badge="burning">
            <text
              x={x - r * 0.72}
              y={y + r * 0.72 + (small ? 7 : 9)}
              textAnchor="middle"
              fontSize={small ? 20 : 26}
            >
              🔥
            </text>
            <text
              x={x - r * 0.72}
              y={y + r * 0.72 + (small ? 3 : 4)}
              textAnchor="middle"
              fontSize={small ? 9 : 11}
              fill="#7a1f00"
              className="font-bold"
            >
              {n.burning}
            </text>
          </g>
        )}
      </g>
    );
  };

  return (
    <svg
      viewBox="0 0 900 900"
      role="group"
      aria-label={label}
      className="h-auto w-full max-w-[720px] select-none"
    >
      <defs>
        {(['ring', 'prod', 'care'] as const).map((m) => (
          <marker
            key={m}
            id={`wheel-arrow-${m}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path
              d="M0,0 L10,5 L0,10 z"
              fill={m === 'ring' ? '#cbd5e1' : m === 'prod' ? WHEEL_COLORS.prod : WHEEL_COLORS.care}
            />
          </marker>
        ))}
      </defs>
      {outer.map((n, i) => (
        <path
          key={`arc-${n.id}`}
          d={arc(R, angle(i) + 8, angle(i + 1) - 8)}
          fill="none"
          stroke="#cbd5e1"
          strokeWidth={2.5}
          markerEnd="url(#wheel-arrow-ring)"
        />
      ))}
      {inner.map((n, k) => (
        <path
          key={`loop-${n.id}`}
          d={arc(INNER_R, innerAngle(k) + 14, innerAngle(k + 1) - 14)}
          fill="none"
          stroke={WHEEL_COLORS.prod}
          strokeWidth={2}
          strokeDasharray="5 4"
          markerEnd="url(#wheel-arrow-prod)"
        />
      ))}
      <path
        d={link(prodAt, NODE_R + 4, firstSub, SUB_R)}
        stroke={WHEEL_COLORS.prod}
        strokeWidth={2}
        strokeDasharray="5 4"
        markerEnd="url(#wheel-arrow-prod)"
      />
      <path
        d={link(lastSub, SUB_R, prodAt, NODE_R + 4)}
        stroke={WHEEL_COLORS.prod}
        strokeWidth={2}
        strokeDasharray="5 4"
        markerEnd="url(#wheel-arrow-prod)"
      />
      <path
        d={link(careAt, NODE_R, firstSub, SUB_R)}
        stroke={WHEEL_COLORS.care}
        strokeWidth={2}
        markerEnd="url(#wheel-arrow-care)"
      />
      <text
        x={(careAt[0] + firstSub[0]) / 2 - 6}
        y={(careAt[1] + firstSub[1]) / 2 - 8}
        textAnchor="middle"
        fontSize={12}
        fill={WHEEL_COLORS.care}
      >
        {changeLabel}
      </text>
      <text x={C} y={C - 4} textAnchor="middle" fontSize={15} className="font-semibold" fill="#1f2937">
        {centerTitle}
      </text>
      <text x={C} y={C + 14} textAnchor="middle" fontSize={11.5} fill="#6b7280">
        {centerSubtitle}
      </text>
      {outer.map((n, i) => node(n, at(R, angle(i)), n.id === production ? NODE_R + 4 : NODE_R, false))}
      {inner.map((n, k) => node(n, at(INNER_R, innerAngle(k)), SUB_R, true))}
    </svg>
  );
}
